import { Box, Typography } from '@mui/joy';
import { AxiosError } from 'axios';
import { FC } from 'react';

import { PrescriptionUtils } from '@MP/features/scan-mini';
import { StimHomePrescriptionUtils } from '@MP/features/stim-home';
import { useDeletePatient } from '@MP/shared/api/hooks/patient';
import { Patient } from '@MP/shared/api/hooks/patient';
import { useFetchPrescriptions } from '@MP/shared/api/hooks/scanMini';
import { useFetchSleepSessions } from '@MP/shared/api/hooks/sleep';
import { useFetchPrescriptions as useFetchStimHomePrescriptions } from '@MP/shared/api/hooks/stimHome';
import AlertDialog from '@MP/shared/components/dialog/AlertDialog';
import ConfirmDialog from '@MP/shared/components/dialog/ConfirmDialog';
import Loading from '@MP/shared/components/Loading';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import MemberDeleteIcon from '@MP/shared/icon/MemberDeleteIcon';
import SleepUtils from '@MP/shared/utils/SleepUtils';

const PatientDeleteConfirmDialog: FC<{
  user: Patient;
  onResult: (user: Patient, result: boolean) => void;
}> = ({ user, onResult }) => {
  const { mutateAsync: deletePatient, isPending } = useDeletePatient();
  const { data: session } = useFetchSleepSessions(user.id);
  const { data: scanMiniPrescriptions } = useFetchPrescriptions(user.id);
  const { data: stimHomePrescriptions } = useFetchStimHomePrescriptions(user.id);
  const snackbar = useMinddSnackbar();

  const notEndSession = session?.filter((v) => !SleepUtils.isDone(v) && !SleepUtils.isCanceled(v));
  const hasInProgress = PrescriptionUtils.hasInProgress(scanMiniPrescriptions);
  const hasStimHomeInProgress = StimHomePrescriptionUtils.hasInProgress(stimHomePrescriptions);

  const handleDelete = async (result: boolean) => {
    if (!result) {
      onResult(user, false);
      return;
    }
    try {
      await deletePatient(user.id);
      snackbar.add(`'${user.name}' 환자 정보가 삭제되었습니다.`);
      onResult(user, true);
    } catch (e) {
      // 404 = 다른 PC 가 이미 삭제한 환자 → 서버엔 없으므로 로컬도 정리(onResult true).
      // 그 외(네트워크·5xx)는 서버에 남아있을 수 있어 로컬 유지, 창만 닫는다(onResult false).
      const status = (e as AxiosError)?.response?.status;
      if (status === 404) {
        snackbar.add('이미 삭제된 환자입니다.');
        onResult(user, true);
      } else {
        snackbar.add('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
        onResult(user, false);
      }
    }
  };

  if (!session || !scanMiniPrescriptions || !stimHomePrescriptions) {
    return <Loading />;
  }

  if (hasInProgress) {
    return (
      <AlertDialog
        open
        warning
        title="환자 정보 삭제"
        message={
          <Box textAlign={'center'}>
            <Typography sx={{ color: '#333' }}>
              {user.name}님의 진행 중인 <b>수면 측정</b>이 있습니다. <br />
              측정 완료 후, 환자 삭제 재시도 바랍니다.
            </Typography>
          </Box>
        }
        onClose={() => onResult(user, false)}
      />
    );
  }

  if (hasStimHomeInProgress) {
    return (
      <AlertDialog
        open
        warning
        title="환자 정보 삭제"
        message={
          <Box textAlign={'center'}>
            <Typography sx={{ color: '#333' }}>
              {user.name}님의 진행 중인 <b>STIM 자극 처방</b>이 있습니다. <br />
              처방 완료 또는 취소 후, 환자 삭제 재시도 바랍니다.
            </Typography>
          </Box>
        }
        onClose={() => onResult(user, false)}
      />
    );
  }

  if (notEndSession && notEndSession?.length > 0) {
    return (
      <AlertDialog
        open
        warning
        title="환자 정보 삭제"
        message={
          <Box textAlign={'center'}>
            <Typography sx={{ color: '#333' }}>
              {user.name}님의 진행 중인 <b>수면 리듬 양상 검사</b>가 있습니다. <br />
              진행 중인 검사를 중단하신 후, 환자 삭제 재시도 바랍니다.
            </Typography>
          </Box>
        }
        onClose={() => onResult(user, false)}
      />
    );
  }

  return (
    <ConfirmDialog
      open
      okColor="danger"
      loading={isPending}
      okText="삭제"
      title="환자 정보 삭제"
      icon={<MemberDeleteIcon width={48} />}
      message={
        <Box textAlign={'center'}>
          <Typography sx={{ color: '#333' }}>
            {user.name}님 환자 정보를 삭제하시겠습니까?
            <br />
            환자 삭제 시 측정 기록이 모두 삭제되며
            <br />
            복구가 불가능합니다.
          </Typography>
        </Box>
      }
      onResult={handleDelete}
    />
  );
};

export default PatientDeleteConfirmDialog;
